// --------------------------------------------------------------
// Created On: 2023-09-20
// Author: Zachary Thomas
//
// Last Modified: 2026-09-14
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, useMemo, Fragment } from "react";
import apiRequest from "../../utilities/api/apiRequest";
import { useParams } from "react-router";
import useReduxSelector from "../../hooks/useReduxSelector";
import useApi from "../../hooks/useApi";
import isoToLocalDate from "../../utilities/time/isoToLocalDate";
import isValueBooleanString from "../../utilities/isValueBooleanString";
import OperationDetailsPanel from "./OperationDetailsPanel/OperationDetailsPanel";
import Card from "../../components/Card/Card";
import {
  API,
  MS_PER_SECOND,
  MAX_QUERIES_AFTER_REMOTE_OPERATION,
  SECONDS_BETWEEN_OPERATION_READ_QUERIES,
  MS_PER_MINUTE,
  MAX_MINUTES_FOR_ACCURATE_READING,
  GATEWAY_TIMEOUT_STATUS,
  DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS,
  DEVICES_THAT_SUPPORT_REMOTE_DEVICE_COMMANDS,
} from "../../constants/miscellaneous";
import { ASSET_DETAILS_POLLING_DELAY_SECONDS, ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS } from "src/constants/polling";
import { POWER_TO_PANEL_ATTRIBUTE, ENGINE_RUNNING_ATTRIBUTE, RPM_ATTRIBUTE } from "../../constants/attributes";
import { LOCK_REMOTE_CONTROL_PERMISSION } from "../../constants/permissions";
import userHasPermission from "../../utilities/userHasPermission";
import getApiError from "../../utilities/api/getApiError";
import Error from "../../components/Error/Error";
import Warning from "../../components/Warning/Warning";
import Gauge from "../../components/Gauge/Gauge";
import RemoteOperationPanel from "./RemoteOperationPanel/RemoteOperationPanel";
import OperationLogList from "./OperationLogList/OperationLogList";
import InfoBanner from "../../components/InfoBanner/InfoBanner";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import deepCopy from "../../utilities/deepCopy";
import Spinner from "../../components/Spinner/Spinner";
import styles from "./AssetRemoteOperationsPage.module.scss";
import Success from "src/components/Success/Success";
import useLastActive from "../../hooks/useLastActive";
import AssetStatusBannerContainer from "src/components/AssetStatusBannerContainer/AssetStatusBannerContainer";

// Page that is used to remotely operating assets. Some possible actions are remote start/stop and speed change.
export default function AssetRemoteOperationsPage(): Component {
  const [initialPageLoading, setInitialPageLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [assetName, setAssetName] = useState<string>("");
  const [operationIsAllowed, setOperationIsAllowed] = useState<boolean>(false);
  const [controllerModel, setControllerModel] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [deviceIdentifier, setDeviceIdentifier] = useState<string | null>(null);
  const [deviceType, setDeviceType] = useState<string | null>(null);
  const [isDeviceAttached, setIsDeviceAttached] = useState<boolean>(false);
  const [isLockedOut, setIsLockedOut] = useState<boolean>(false);
  const [canUnlock, setCanUnlock] = useState<boolean>(false);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [isRented, setIsRented] = useState<boolean>(false);
  const [lastConfigAttemptUtc, setLastConfigAttemptUtc] = useState<string | null>(null);
  const [lastUpdateFirmwareAttemptUtc, setLastUpdateFirmwareAttemptUtc] = useState<string | null>(null);
  const [lastComm, setLastComm] = useState<string | null>(null);
  const [firmwareStatus, setFirmwareStatus] = useState<Status>("SUCCEEDED");
  const [previousFirmwareStatus, setPreviousFirmwareStatus] = useState<Status>("SUCCEEDED");
  const [configurationStatus, setConfigurationStatus] = useState<Status>("SUCCEEDED");
  const [previousConfigurationStatus, setPreviousConfigurationStatus] = useState<Status>("SUCCEEDED");
  const [isSampling, setIsSampling] = useState<boolean>(false);
  const [onLatestFirmware, setOnLatestFirmware] = useState<boolean | null>(null);
  const [showConfirmLock, setShowConfirmLock] = useState<boolean>(false);
  const [showConfirmUnlock, setShowConfirmUnlock] = useState<boolean>(false);
  const [firmwareUpdateInitialized, setFirmwareUpdateInitialized] = useState<boolean>(false);
  const [remoteOperationSupported, setRemoteOperationSupported] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [controllerApplicationType, setControllerApplicationType] = useState<string | null>(null);
  const [operationLogs, setOperationLogs] = useState<OperationLog[]>([]);
  const [listAttributes, setListAttributes] = useState<ListAttribute[]>([]);
  const [gaugeAttributes, setGaugeAttributes] = useState<GaugeAttribute[]>([]);
  const [digitalOutputs, setDigitalOutputs] = useState<DigitalOutput[]>([]);
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [dataPollCounter, setDataPollCounter] = useState<number>(0);
  const [timerCounter, setTimerCounter] = useState<number>(0);
  const userInactiveSeconds = useLastActive(ASSET_DETAILS_POLLING_DELAY_SECONDS);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const { assetId } = useParams();
  const userCanOperate = DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(deviceType || "") && operationIsAllowed;
  const staleAttributeCodes = useMemo(
    () => getStaleAttributeCodes([...listAttributes, ...gaugeAttributes]),
    [JSON.stringify(listAttributes), JSON.stringify(gaugeAttributes)]
  );

  // Get the controller app type, operation logs, and the current relevant attribute readings.
  useApi(
    () => {
      if (dataPollCounter === 0) {
        setInitialPageLoading(true);
      }
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}/operation`,
    },
    async (response: Response, responseBody: GetResponseBody) => {
      if (response.ok && responseBody) {
        setControllerApplicationType(responseBody.controllerApplicationType);
        setOperationLogs(responseBody.assetOperationLogs);
        setListAttributes(responseBody.listAttributes);
        setGaugeAttributes(responseBody.gaugeAttributes);
        setDigitalOutputs(responseBody.digitalOutputs);
        setAssetName(responseBody.assetName);
        setOperationIsAllowed(responseBody.operationIsAllowed);
        setControllerModel(responseBody.controllerModel);
        setDeviceId(responseBody.deviceId);
        setDeviceIdentifier(responseBody.deviceIdentifier);
        setDeviceType(responseBody.deviceType);
        setIsDeviceAttached(responseBody.isDeviceAttached);
        setIsLockedOut(responseBody.isLockedOut);
        setCanUnlock(responseBody.canUnlock);
        setIsMigrating(responseBody.isMigrating);
        setIsRented(responseBody.isRented);
        setLastComm(responseBody.lastComm);
        setLastConfigAttemptUtc(responseBody.lastConfigAttemptUtc);
        if (responseBody.lastUpdateFirmwareAttemptUtc !== undefined) {
          setLastUpdateFirmwareAttemptUtc(responseBody.lastUpdateFirmwareAttemptUtc);
        }
        setOnLatestFirmware(responseBody.onLatestFirmware);
        setRemoteOperationSupported(
          responseBody.remoteOperationSupported &&
            DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(responseBody.deviceType || "")
        );

        // Due to this API endpoint being continuously polled, this logic may be reached before the configuration or
        // firmware update is complete.
        // We want to avoid overwriting the previous configuration or firmware status if it is in progress so that
        // success and failure banners stay on the page if the configuration or firmware update is applied while the
        // user is on the page.
        // If we do not do this, any success or failure banners that display to the user in response to applying
        // configuration or updating firmware will disappear the next time data is polled.
        if (previousConfigurationStatus !== "IN_PROGRESS") {
          setPreviousConfigurationStatus(configurationStatus);
        }
        if (responseBody.configurationStatus !== undefined && responseBody.configurationStatus !== null) {
          setConfigurationStatus(responseBody.configurationStatus);
        }

        if (previousFirmwareStatus !== "IN_PROGRESS") {
          setPreviousFirmwareStatus(firmwareStatus);
        }
        if (responseBody.firmwareStatus !== undefined && responseBody.firmwareStatus !== null) {
          setFirmwareStatus(responseBody.firmwareStatus);
        }
      } else {
        console.error("Internal server error. Unable to retrieve operational data.");
      }
      setInitialPageLoading(false);
    },
    [assetId, dataPollCounter]
  );

  // Periodically poll data to give users a close to live feed (does not actually sample, unlike a real time read).
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setTimerCounter((prev) => prev + 1);
      // Only poll data if the user is active.
      console.log(
        `Looking to see if data should be polled` +
          ` (inactive for ${userInactiveSeconds} seconds of ${ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS} second limit).`
      );
      if (userInactiveSeconds < ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS) {
        setDataPollCounter((prev) => prev + 1);
      } else {
        console.log("User is inactive. Data will not be polled.");
      }
    }, ASSET_DETAILS_POLLING_DELAY_SECONDS * MS_PER_SECOND);
    return () => {
      clearTimeout(newTimerId);
    };
  }, [timerCounter]);

  // Temporarily poll displayed data after a remote operation to take as close to live readings as possible.
  useEffect(() => {
    if (queriesRemaining > 0) {
      const timerId = setTimeout(
        () => sampleDevice(assetId, queriesRemaining),
        MS_PER_SECOND * SECONDS_BETWEEN_OPERATION_READ_QUERIES
      );

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [queriesRemaining, assetId]);

  // Sample device to get the most current device data.
  async function sampleDevice(assetId: string | undefined, queriesRemaining: number): Promise<void> {
    if (assetId !== undefined && queriesRemaining > 0) {
      const [response, responseBody] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/live`,
        "GET",
        null
      )) as [Response, GetAttributesResponseBody];

      if (response.ok) {
        // Since we got new values for existing attributes, update the existing attributes.
        const listAttributesDeepCopy = deepCopy(listAttributes);
        const gaugeAttributesDeepCopy = deepCopy(gaugeAttributes);
        let attributeChanged = false;

        responseBody.attributes.forEach((updatedAttribute) => {
          const foundAttribute = [...listAttributesDeepCopy, ...gaugeAttributesDeepCopy].find(
            (attribute) => updatedAttribute.regAttributeId === attribute.regAttributeId
          );
          if (
            foundAttribute !== undefined &&
            updatedAttribute.currentValueUtc !== null &&
            foundAttribute.currentValueUtc !== null &&
            updatedAttribute.currentValueUtc > foundAttribute.currentValueUtc
          ) {
            foundAttribute.currentValue = updatedAttribute.currentValue;
            foundAttribute.currentValueUtc = updatedAttribute.currentValueUtc;
            attributeChanged = true;
          }
        });
        if (attributeChanged) {
          setListAttributes(listAttributesDeepCopy);
          setGaugeAttributes(gaugeAttributesDeepCopy);
        }
        setQueriesRemaining(queriesRemaining - 1);
      } else {
        // If we get an error, stop trying to process a device update.
        setQueriesRemaining(0);
      }
    }
  }

  // Change communication mode.
  async function changeCommunicationMode(command: string): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    const requestBody = { command: command };
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/commsmode`,
      "POST",
      requestBody
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Communication mode command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to change communication mode."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();
  }

  // Change running status.
  async function changeRunningStatus(command: string): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    const requestBody = { command: command };
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/runningstatus`,
      "POST",
      requestBody
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Running status command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to change running status."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();

    // Perform live reads for a bit after this operation.
    setQueriesRemaining(MAX_QUERIES_AFTER_REMOTE_OPERATION);
  }

  // Change speed.
  async function changeSpeed(command: number): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    const requestBody = { command: command };
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/speed`,
      "POST",
      requestBody
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Change speed command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to change speed."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();

    // Perform live reads for a bit after this operation.
    setQueriesRemaining(MAX_QUERIES_AFTER_REMOTE_OPERATION);
  }

  // Change digital output status based on the specified position.
  async function changeDigitalOutput(position: number, state: boolean): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");

    const requestBody = {
      position: position,
      state: state,
    };

    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/digital-output`,
      "POST",
      requestBody
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Digital output command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to change digital output status."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();

    // Perform live reads for a bit after this operation.
    setQueriesRemaining(MAX_QUERIES_AFTER_REMOTE_OPERATION);
  }

  // Updates the migration state based on if the asset has successfully completed migration or not.
  async function handleMigrateAsset(status: Status, previousStatus: Status): Promise<void> {
    if (status === "SUCCEEDED" && previousStatus === "IN_PROGRESS") {
      // Consider the migration completed if a migration was in progress and is now successful.
      setIsMigrating(false);
    } else {
      // Consider the migration incomplete if a migration is still in progress.
      setIsMigrating(true);
    }
    void updateOperationLogs();
  }

  // Updates the configuration status based on if migrating an asset or applying the configuration successfully started.
  function handleApplyConfiguration(status: Status): void {
    setLastConfigAttemptUtc(null);
    setPreviousConfigurationStatus(configurationStatus);
    setConfigurationStatus(status);
    void updateOperationLogs();
  }

  // Updates the firmware status based on if updating a device's firmware successfully started.
  // When a firmware update is initialized, lock remote operation controls and update the status logs.
  function handleUpdateFirmware(status: Status): void {
    setLastUpdateFirmwareAttemptUtc(null);
    setFirmwareUpdateInitialized(true);
    setPreviousFirmwareStatus(firmwareStatus);
    setFirmwareStatus(status);
    void updateOperationLogs();
  }

  // Changes a valve's position by a certain percent.
  async function changeValvePosition(valveNumber: number, percentOpen: number): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    setQueriesRemaining(0);
    setLoading(true);
    const requestBody = {
      valveNumber: valveNumber,
      percentOpen: percentOpen,
    };
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/open-valve`,
      "POST",
      requestBody
    )) as [Response, MessageResponseBody];
    setLoading(false);

    if (response.ok && responseBody) {
      setSuccessMessage("Valve position command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to change valve position."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();

    // Perform live reads for a bit after this operation.
    setQueriesRemaining(MAX_QUERIES_AFTER_REMOTE_OPERATION);
  }

  // Clear failed commands from monitoring device.
  async function handleClearFailedCommands(): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/clearalerts`,
      "POST",
      null
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Clear device command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to clear failed commands."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();
  }

  // Restart monitoring device.
  async function handleRestartMonitoringDevice(): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/reset`,
      "POST",
      null
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Restart device command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to restart monitoring device."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();
  }

  // Reapplies a device's configuration.
  async function handleReapplyConfiguration(): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/reapply-config`,
      "POST",
      null
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Reapply configuration command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to reapply configurations."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();
  }

  // Blink LEDs on monitoring device.
  async function handleBlinkLeds(pattern: string, position: string): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    setQueriesRemaining(0);
    setLoading(true);
    const requestBody = {
      ledPattern: pattern,
      ledPosition: position,
    };
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/blink-led`,
      "POST",
      requestBody
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Blink LEDs command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to blink LEDs on the monitoring device."));
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();
  }

  // Tests applying a configuration to the Co-Pilot Edge. This is strictly for development purposes.
  async function handleTestConfiguration(): Promise<void> {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/set-configuration`,
      "POST",
      null
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setSuccessMessage("Test configuration command sent successfully.");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setWarningMessage("Request timeout. The command's status could not be confirmed. Please try again.");
    } else {
      setErrorMessage(
        await getApiError(response, "Failed while attempting to test configuration on the monitoring device.")
      );
    }

    // Get the most recent operation logs since we just performed an operation.
    void updateOperationLogs();
  }

  // Get the operation logs for the current asset and save them to state.
  async function updateOperationLogs(): Promise<void> {
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operatelog`,
      "GET",
      null
    )) as [Response, GetLogsResponseBody];
    if (response.ok) {
      setOperationLogs(responseBody.assetOperationLogs);
    }
  }

  // Handle attempts to update the lock status.
  function handleLockStatus(isLocked: boolean): void {
    if (isLocked) {
      setShowConfirmLock(true);
    } else {
      setShowConfirmUnlock(true);
    }
  }

  // Make API call to update lock status.
  async function updateLockStatus(isLocked: boolean): Promise<void> {
    setShowConfirmLock(false);
    setShowConfirmUnlock(false);
    setSuccessMessage("");
    const requestBody = { lockout: isLocked };
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/lockout`,
      "PUT",
      requestBody
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setIsLockedOut(isLocked);
    } else {
      setErrorMessage(await getApiError(response, "Failed while attempting to change lock for remote operations."));
    }
  }

  // Returns an array of attribute codes that represent attributes with stale data.
  function getStaleAttributeCodes(attributes: (ListAttribute | GaugeAttribute)[]): string[] {
    const staleAttributeCodes: string[] = [];
    const nowDate = new Date();

    // Mark attributes as stale if they haven't reported a new reading in a certain amount of time.
    attributes.forEach((attribute) => {
      if (attribute.currentValueUtc === null) {
        staleAttributeCodes.push(attribute.attributeCode);
      } else {
        const attributeDate = isoToLocalDate(attribute.currentValueUtc);
        const attributeIsStale =
          (nowDate.getTime() - attributeDate.getTime()) / MS_PER_MINUTE > MAX_MINUTES_FOR_ACCURATE_READING;
        if (attributeIsStale) {
          staleAttributeCodes.push(attribute.attributeCode);
        }
      }
    });

    // Some attributes values indicate that other attributes may not have a reliable value.
    // In these cases we will mark attributes as stale if they cannot be trusted.
    const powerToPanelAttribute = attributes.find((attribute) => attribute.attributeCode === POWER_TO_PANEL_ATTRIBUTE);

    const engineRunningAttribute = attributes.find((attribute) => attribute.attributeCode === ENGINE_RUNNING_ATTRIBUTE);

    if (
      !staleAttributeCodes.includes(ENGINE_RUNNING_ATTRIBUTE) &&
      powerToPanelAttribute !== undefined &&
      powerToPanelAttribute.currentValue !== null &&
      isValueBooleanString(powerToPanelAttribute.currentValue) &&
      powerToPanelAttribute.currentValue.toLowerCase() === "false"
    ) {
      staleAttributeCodes.push(ENGINE_RUNNING_ATTRIBUTE);
    }

    if (
      !staleAttributeCodes.includes(RPM_ATTRIBUTE) &&
      ((powerToPanelAttribute !== undefined &&
        powerToPanelAttribute.currentValue !== null &&
        isValueBooleanString(powerToPanelAttribute.currentValue) &&
        powerToPanelAttribute.currentValue.toLowerCase() === "false") ||
        (engineRunningAttribute !== undefined &&
          engineRunningAttribute.currentValue !== null &&
          isValueBooleanString(engineRunningAttribute.currentValue) &&
          engineRunningAttribute.currentValue.toLowerCase() === "false"))
    ) {
      staleAttributeCodes.push(RPM_ATTRIBUTE);
    }

    return staleAttributeCodes;
  }

  return (
    <div className="p-4 mx-auto">
      {gaugeAttributes.length > 0 && (
        <div className="row align-items-center">
          {gaugeAttributes.map((attribute, i) => (
            <div className={`col text-center ${styles.gaugeColumn}`} key={i}>
              <Gauge
                code={attribute.attributeCode}
                label={attribute.attributeName}
                min={attribute.minValue}
                max={attribute.maxValue}
                value={attribute.currentValue}
                time={attribute.currentValueUtc}
                unitShortName={attribute.unitShortName}
                staleAttributeCodes={staleAttributeCodes}
                loading={queriesRemaining > 0}
              />
            </div>
          ))}
        </div>
      )}

      {errorMessage.length === 0 &&
        !userCanOperate &&
        !isMigrating &&
        !loading &&
        !initialPageLoading &&
        !operationIsAllowed && (
          <div className="my-4 px-3">
            <InfoBanner message="You do not have permission to remotely operate this asset." />
          </div>
        )}

      <div className="my-4 px-3">
        <AssetStatusBannerContainer
          loading={loading || initialPageLoading}
          assetId={assetId ?? ""}
          deviceId={deviceId}
          firmwareStatus={firmwareStatus}
          configurationStatus={configurationStatus}
          previousFirmwareStatus={previousFirmwareStatus}
          previousConfigurationStatus={previousConfigurationStatus}
          deviceIdentifier={deviceIdentifier ?? ""}
          deviceTypeName={deviceType ?? ""}
          isMigrating={isMigrating}
          isRented={isRented}
          onLatestFirmware={onLatestFirmware}
          lastConfigAttemptUtc={lastConfigAttemptUtc}
          lastUpdateFirmwareAttemptUtc={lastUpdateFirmwareAttemptUtc}
          lastComm={lastComm}
          hideLastComm={true}
          onChangeLoading={(loading) => setLoading(loading)}
          onMigrateAsset={(status, previousStatus) => handleMigrateAsset(status, previousStatus)}
          onApplyConfiguration={(status) => handleApplyConfiguration(status)}
          onUpdateFirmware={(status) => handleUpdateFirmware(status)}
        />
      </div>

      {successMessage.length > 0 && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Success message={successMessage} />
        </div>
      )}

      {errorMessage.length > 0 && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Error message={errorMessage} />
        </div>
      )}

      {!initialPageLoading &&
        (deviceId === null || !DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(deviceType || "")) &&
        !userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]]) && (
          <div className={`${styles.stickyMessage} my-4 px-3`}>
            <Error message="This asset does not support remote control operation due to its monitoring device / controller configuration." />
          </div>
        )}

      {warningMessage.length > 0 && (
        <div className={`${styles.stickyMessage} my-4 px-3`}>
          <Warning message={warningMessage} />
        </div>
      )}

      <div className="row justify-content-center mt-2 mb-4 gx-0 mx-0">
        {(listAttributes.length > 0 || digitalOutputs.length > 0) && (
          <div
            className={userCanOperate || listAttributes.length > 0 ? "col-12 col-lg-6 col-xl-8 mb-4 mb-lg-0" : "col-12"}
          >
            <OperationDetailsPanel
              assetId={assetId === undefined ? 0 : parseInt(assetId, 10)}
              attributes={listAttributes}
              staleAttributeCodes={staleAttributeCodes}
              digitalOutputs={digitalOutputs}
              deviceId={deviceId}
              deviceType={deviceType}
              userCanOperate={userCanOperate}
              dataPollCounter={dataPollCounter}
              loading={loading || initialPageLoading || isMigrating}
              onIsSamplingChange={(isSampling) => setIsSampling(isSampling)}
            />
          </div>
        )}

        {/* We show remote operation controls only if the asset is not locked out and has a valid device. */}
        {!isLockedOut &&
          operationIsAllowed &&
          (DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(deviceType || "") ||
            DEVICES_THAT_SUPPORT_REMOTE_DEVICE_COMMANDS.includes(deviceType || "") ||
            userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]])) && (
            <div className="col-12 col-lg-6 col-xl-4 mb-4 mb-lg-0">
              <RemoteOperationPanel
                assetName={assetName}
                deviceId={deviceId}
                deviceType={deviceType}
                isDeviceAttached={isDeviceAttached}
                isMigrating={isMigrating}
                remoteOperationSupported={remoteOperationSupported}
                controllerApplicationType={controllerApplicationType}
                controllerModel={controllerModel}
                isLockedOut={isLockedOut}
                canUnlock={canUnlock}
                loading={loading || initialPageLoading || isSampling}
                firmwareUpdateInitialized={firmwareUpdateInitialized}
                onLatestFirmware={onLatestFirmware}
                digitalOutputs={digitalOutputs}
                onChangeCommunicationMode={(command) => changeCommunicationMode(command)}
                onChangeSpeed={(command) => changeSpeed(command)}
                onChangeRunningStatus={(command) => changeRunningStatus(command)}
                onClearFailedCommands={() => handleClearFailedCommands()}
                onRestartMonitoringDevice={() => handleRestartMonitoringDevice()}
                onReapplyConfiguration={() => handleReapplyConfiguration()}
                onBlinkLeds={(pattern, position) => handleBlinkLeds(pattern, position)}
                onTestConfiguration={() => handleTestConfiguration()}
                onError={(error) => setErrorMessage(error)}
                onChangeLock={(isLocked) => handleLockStatus(isLocked)}
                onChangeValvePosition={(valveNumber, percentOpen) => changeValvePosition(valveNumber, percentOpen)}
                onChangeDigitalOutput={(position, state) => changeDigitalOutput(position, state)}
              />
            </div>
          )}

        {/* We show this message if remote operation is locked for this asset. */}
        {!loading && !initialPageLoading && isLockedOut && (
          <div className={listAttributes.length > 0 ? "col-12 col-lg-6 col-xl-4 mb-4 mb-lg-0" : "col-12"}>
            <Card title={`${assetName} Remote Operations`}>
              <p className="h4 text-center py-5 px-4">
                Remote control access is locked for this asset.
                {canUnlock
                  ? ""
                  : " The lock was applied by the company you are renting this asset from, only they can unlock this asset."}
              </p>
              <Fragment>
                {canUnlock && userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]]) && (
                  <div className="text-center">
                    <button
                      className="btn btn-primary mx-auto mb-5"
                      type="button"
                      disabled={isMigrating}
                      onClick={() => handleLockStatus(false)}
                    >
                      Unlock Remote Control
                    </button>
                  </div>
                )}
              </Fragment>
            </Card>
          </div>
        )}
      </div>

      {operationLogs.length > 0 && <OperationLogList assetName={assetName} operationLogs={operationLogs} />}

      <ConfirmModal
        showModal={showConfirmLock}
        title="Apply Remote Lock?"
        content={
          "Are you sure that you want to apply a remote lock on this asset? " +
          "This will prevent all remote operations for this asset until the lock is removed."
        }
        yesText="Apply Remote Lock"
        noText="Cancel"
        danger={false}
        onClose={() => setShowConfirmLock(false)}
        onYes={() => updateLockStatus(true)}
        onNo={() => setShowConfirmLock(false)}
      />

      <ConfirmModal
        showModal={showConfirmUnlock}
        title="Remove Remote Lock?"
        content="Are you sure that you want to remove the remote lock on this asset?"
        yesText="Remove Remote Lock"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmUnlock(false)}
        onYes={() => updateLockStatus(false)}
        onNo={() => setShowConfirmUnlock(false)}
      />

      <Spinner loading={loading || initialPageLoading} />
    </div>
  );
}

interface PostResponseBody {
  message: string;
}

interface GetResponseBody {
  controllerApplicationType: string;
  assetOperationLogs: OperationLog[];
  listAttributes: ListAttribute[];
  gaugeAttributes: GaugeAttribute[];
  assetName: string;
  operationIsAllowed: boolean;
  controllerModel: string | null;
  deviceId: number | null;
  deviceIdentifier: string | null;
  deviceType: string | null;
  isDeviceAttached: boolean;
  isLockedOut: boolean;
  canUnlock: boolean;
  isMigrating: boolean;
  isRented: boolean;
  lastConfigAttemptUtc: string | null;
  lastSuccessfulConfigUtc: string | null;
  lastUpdateFirmwareAttemptUtc: string | null;
  lastUpdateFirmwareSuccessfulUtc: string | null;
  lastComm: string | null;
  configurationStatus: Status | null;
  firmwareStatus: Status | null;
  onLatestFirmware: boolean | null;
  remoteOperationSupported: boolean;
  digitalOutputs: DigitalOutput[];
}

interface GetLogsResponseBody {
  assetOperationLogs: OperationLog[];
}

interface GetAttributesResponseBody {
  attributes: ListAttribute[];
}

interface MessageResponseBody {
  message: string;
}

interface OperationLog {
  assetOperationLogId: number;
  commandType: string;
  value: string;
  commandSucceeded: boolean;
  username: string;
  emailAddress: string;
  operationUtc: string;
}

interface ListAttribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  unitShortName: string;
  currentValue: string | null;
  currentValueUtc: string | null;
}

interface GaugeAttribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  unitShortName: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  minValue: number;
  maxValue: number;
}

interface DigitalOutput {
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
  currentValue: boolean | null;
  currentValueUtc: string | null;
}

type Status = "IN_PROGRESS" | "SUCCEEDED" | "FAILED";
